import { useStore } from 'stimulus-store';
import TomSelect from "tom-select";
import { getRoomsForSelect } from "shared/locales/locales";
import { updateSelect } from "shared/selects/select_helpers";
import _, { isEmpty } from "lodash";
import { Controller } from "@hotwired/stimulus";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { areaStore } from "../../../stores/locales/area_store";
import { roomStore } from "../../../stores/locales/room_store";

// Connects to data-controller="selects--locales--room-select"
//
// TURBO DRIVE NOTE: roomStore is a module-level singleton that persists across
// Turbo navigations. This controller clears it in disconnect() when this
// controller's element is torn down (e.g. on Turbo Drive navigation). See
// region_select_controller.js for the full rationale.
export default class extends Controller {
    _region
    _building
    _area

    static stores = [
        areaStore,
        roomStore
    ]

    static values = {
        parentArea: String,
        parentBuilding: String,
        parentRegion: String,
        selected: String,
        url: String,

        // See region_select_controller.js for the full rationale: set only by pages
        // that need organization-wide (not active-project) results, e.g. the Reports
        // filter builder. Left unset, this controller's behavior is unchanged.
        projectScopeToken: String
    }

    connect() {
        useStore(this);

        // Listen for room creation events
        window.addEventListener('roomCreated', this.handleRoomCreation.bind(this));
    }

    disconnect() {
        // Clean up event listener
        window.removeEventListener('roomCreated', this.handleRoomCreation.bind(this));

        // Clear this controller's own store on teardown. Note this only resets
        // roomStore, not areaStore -- this controller subscribes to areaStore
        // (to react to area changes) but doesn't own it; area_select_controller
        // is responsible for clearing areaStore in its own disconnect().
        this.resetRoomStore();
    }

    initialize() {
        this._createRoomSelect();
    }

    _createRoomSelect() {
        // Get the room select and the region, building, and area ids
        const roomSelectElement = this.element;
        this._region = this.parentRegionValue;
        this._building = this.parentBuildingValue;
        this._area = this.parentAreaValue;
        const selectedRoomId = this.selectedValue;

        // Require a region and building to be selected to fetch areas
        if (!_.isEmpty(this._region) && !_.isEmpty(this._building) && !_.isEmpty(this._area)) {
            // Fetch this region's buildings
            getRoomsForSelect(this._region, this._building, this._area, this.projectScopeTokenValue).then((results) => {
                const control = new TomSelect(roomSelectElement, {
                    placeholder: 'Select a room',
                    create: false,
                    allowClear: true,
                    valueField: 'id',
                    labelField: 'text',
                    searchField: 'text',
                    sortField: {
                        field: "text",
                        direction: "asc"
                    },
                    onChange: (event) => this._roomSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                    onDelete: (event) => this._roomUnselectedEvent(event),  // Keep this separate or page loads will be broken
                    maxOptions: null, // tom-select will truncate the select to 50 options by default
                    plugins: ['remove_button'], // This enables a "clear" button on selected items
                    closeAfterSelect: true // This closes the select after a selection is made.
                });

                // Add the expected options to the select. We're doing it this way instead of
                // in the constructor so the items appear in the underlying select. This makes
                // it possible to test using capybara matchers
                updateSelect(this.element.id, results);

                // Syncing will ensure options are stubbed out within the select element. This
                // ensures the tests will have the data in the DOM it can test against
                control.sync();

                // If a room is already selected, select it!
                if (selectedRoomId !== null) {
                    // Setting silent to true suppresses change events
                    // https://tom-select.js.org/docs/api/
                    control.setValue(selectedRoomId, DO_NOT_EMIT_EVENTS);

                    // Inform others what room is selected
                    const selectedOption = control.options[selectedRoomId];
                    const roomName = selectedOption.text

                    this.setRoomStoreValue({
                        id: selectedRoomId,
                        name: roomName
                    });
                }
            });
        } else {
            const control = new TomSelect(roomSelectElement, {
                placeholder: 'Select a room',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'text',
                searchField: 'text',
                sortField: {
                    field: "text",
                    direction: "asc"
                },
                onChange: (event) => this._roomSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (event) => this._roomUnselectedEvent(event),  // Keep this separate or page loads will be broken
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                closeAfterSelect: true // This closes the select after a selection is made.
            });

            // Disable the building select since the user hasn't selected a region, building, or area yet
            this.element.disabled = true;
            if (this.element.tomselect) {
                this.element.tomselect.disable();
            }
        }

    }

    _roomSelectionChanged(roomId) {
        // We only want to process events where the user made a selection. Ignore
        // other events. This happens during initialization.
        if (isEmpty(roomId)) {
            return;
        }

        // Get the selected option text for the room name
        const selectedOption = this.element.tomselect.options[roomId];
        const roomName = selectedOption ? selectedOption.text : `Room ${ roomId }`;

        // Update the store with the room's id and name
        this.setRoomStoreValue({
            id: roomId,
            name: roomName
        });

        // Send an event telling the other selects/elements that a model has been
        // selected. I stole this method from here:
        // https://lmika.org/2021/02/17/communication-among-stimulus.html
        window.dispatchEvent(new CustomEvent('roomSelectionMade', {
            detail: {
                region: this._region,
                building: this._building,
                area: this._area,
                room: roomId
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));
    }

    _roomUnselectedEvent(values) {
        // Clear the room store
        this.setRoomStoreValue({});

        // If we don't have an ID, clear out the current selections and return
        window.dispatchEvent(new Event('roomSelectionCleared', {
            detail: {},
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));
    }

    handleRegionChanged(event) {
        // Return if this event is empty. This may happen during initialization.
        if (event === undefined) {
            return;
        }

        // Get the new selected region ID from the event
        this._region = event.detail.region;
        this._building = null;
        this._area = null;

        // Disable the room select since area is no longer selected
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear out this select since this must mean the parent area was also cleared
        updateSelect(this.element.id, []);
    }

    handleRegionCleared(event) {
        // Clear the variable that stores the region, building, and area ids
        this._region = null;
        this._building = null;
        this._area = null;

        // Disable the room select
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear the rooms
        updateSelect(this.element.id, []);
    }

    handleBuildingChange(event) {
        // Update the selected building
        this._building = event.detail.building;
        this._area = null;

        // Disable the room select since area is no longer selected
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear out this select since this must mean the parent area was also cleared
        updateSelect(this.element.id, []);
    }

    handleBuildingCleared(event) {
        // Clear the variable that stores the building id
        this._building = null;
        this._area = null;

        // Disable the room select
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear the buildings
        updateSelect(this.element.id, []);
    }

    handleAreaChange(event) {
        // Update the selected building
        this._area = event.detail.area;

        // Get the new area's rooms
        getRoomsForSelect(this._region, this._building, event.detail.area, this.projectScopeTokenValue).then((results) => {
            updateSelect(this.element.id, results)

            // Clear the existing room selection.
            this.element.tomselect.clear(DO_NOT_EMIT_EVENTS)
            this.setRoomStoreValue({});

            // Enable the room select only once its options are actually
            // populated. Enabling synchronously before this resolved meant a
            // fast click (real user or automated test) could open the
            // dropdown while it was still empty.
            this.element.disabled = false;
            if (this.element.tomselect) {
                this.element.tomselect.enable();
            }
        })
    }

    handleAreaCleared(event) {
        // Clear the variable that stores the building id
        this._area = null;

        // Disable the room select
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear the buildings
        updateSelect(this.element.id, []);

        // Clear the existing room selection.
        this.element.tomselect.clear(DO_NOT_EMIT_EVENTS)
        this.setRoomStoreValue({});
    }

    onAreaStoreUpdate(event) {
        if (!isEmpty(event.id) && !_.isEmpty(event.building) || !_.isEmpty(event.region)) {
            // Update the selected area
            this._area = event.id;

            // Fetch this area's rooms
            getRoomsForSelect(event.region, event.building, event.id, this.projectScopeTokenValue).then((results) => {
                updateSelect(this.element.id, results)

                // If there happens to be a selected room, select it
                if (!_.isEmpty(this.roomStoreValue?.id)) {
                    // Setting silent to true suppresses change events
                    // https://tom-select.js.org/docs/api/
                    this.element.tomselect.setValue(this.roomStoreValue?.id, DO_NOT_EMIT_EVENTS);
                }

                // Enable the room select only once its options are actually
                // populated. Enabling synchronously before this resolved meant
                // a fast click (real user or automated test) could open the
                // dropdown while it was still empty.
                this.element.disabled = false;
                if (this.element.tomselect) {
                    this.element.tomselect.enable();
                }
            })
        } else {
            // Disable the room select
            this.element.disabled = true;
            if (this.element.tomselect) {
                this.element.tomselect.disable();
            }

            // Clear the rooms
            updateSelect(this.element.id, []);
        }
    }

    onRoomStoreUpdate(event) {
        if (event && event?.id) {
            // Setting silent to true suppresses change events
            // https://tom-select.js.org/docs/api/
            this.element.tomselect.setValue(event?.id, DO_NOT_EMIT_EVENTS);
        } else if (event && event.id === null) {
            // Set the selected value to empty. This selects the placeholder.
            this.element.tomselect.setValue('', DO_NOT_EMIT_EVENTS);
        }
    }

    /**
     * Handle room creation events from locale form controller
     * Adds newly created room to the select dropdown
     */
    handleRoomCreation(event) {
        const { id, name } = event.detail;

        if (!id || !name) {
            console.warn('Room creation event missing id or name:', event.detail);
            return;
        }

        // Only add room if we have an area selected (rooms belong to areas)
        if (!this._area) {
            console.warn('Cannot add room - no area selected');
            return;
        }

        // Check if TomSelect is initialized and enabled
        if (!this.element.tomselect) {
            console.warn('TomSelect not initialized, cannot add new room');
            return;
        }

        if (this.element.disabled) {
            console.warn('Room select is disabled, cannot add new room');
            return;
        }

        try {
            // Add the new room option to TomSelect
            this.element.tomselect.addOption({
                id: id,
                text: name,
                value: id
            });

        } catch (error) {
            console.error('Error adding new room to select:', error);
        }
    }
}
